import { createDesktopCloudApiTransport } from '@/desktop/desktopCloudTransport';
import { createProjectApi, projectApi } from '@/services/ProjectApi';
import {
  type WorkAreaDataSourceRegistry,
  workAreaDataSourceRegistry,
} from './WorkAreaDataSourceRegistry';

export type ProjectContentDataSource = Pick<
  typeof projectApi,
  | 'workAreaDocuments'
  | 'recentWorkAreaDocuments'
  | 'workAreaDocumentCheckpoint'
  | 'workAreaDocument'
  | 'createWorkAreaDocument'
  | 'updateWorkAreaDocument'
  | 'removeWorkAreaDocument'
  | 'attachWorkAreaDocument'
  | 'detachWorkAreaDocument'
  | 'dashboardPageDocuments'
  | 'workItemDocuments'
  | 'partDocuments'
  | 'workAreaDashboardPages'
  | 'workAreaDashboardPageCheckpoint'
  | 'createWorkAreaDashboardPage'
  | 'updateWorkAreaDashboardPage'
  | 'setWorkAreaDashboardPageShares'
  | 'reorderWorkAreaDashboardPages'
  | 'moveWorkAreaDashboardPage'
  | 'removeWorkAreaDashboardPage'
>;

export type ProjectContentCrossWorkAreaDataSource = Pick<
  typeof projectApi,
  'recentWorkAreaDocuments'
>;

export class ProjectContentDataSourceRegistry {
  private readonly desktopCloudSources = new Map<string, ProjectContentDataSource>();

  constructor(
    private readonly workAreaRegistry: WorkAreaDataSourceRegistry = workAreaDataSourceRegistry,
    private readonly cloudSource: ProjectContentDataSource = projectApi,
    private readonly desktopCloudSourceFactory: (
      desktopWorkAreaId: string,
    ) => ProjectContentDataSource = (desktopWorkAreaId) =>
      createProjectApi(createDesktopCloudApiTransport(desktopWorkAreaId)),
  ) {}

  /**
   * The source that answers for many work areas at once, and which of `projectIds` it reaches
   * (I-0726). A work area bound to a desktop connection answers only for itself.
   */
  crossWorkAreaSource(projectIds: readonly string[]): {
    source: ProjectContentCrossWorkAreaDataSource;
    projectIds: string[];
  } {
    return {
      source: this.cloudSource,
      projectIds: projectIds.filter((projectId) => this.forProject(projectId) === this.cloudSource),
    };
  }

  forProject(projectId: string): ProjectContentDataSource {
    const descriptor = this.workAreaRegistry.descriptor(projectId);
    if (!descriptor) {
      return this.cloudSource;
    }
    const desktopWorkAreaId = descriptor.desktopBinding?.desktopWorkAreaId;
    if (!desktopWorkAreaId) return this.cloudSource;
    let source = this.desktopCloudSources.get(desktopWorkAreaId);
    if (!source) {
      source = this.desktopCloudSourceFactory(desktopWorkAreaId);
      this.desktopCloudSources.set(desktopWorkAreaId, source);
    }
    return source;
  }
}

export const projectContentDataSourceRegistry = new ProjectContentDataSourceRegistry();
